<template>
    <!-- The app's QR reader. It used to be a page of its own (`/qr`), which
         meant leaving the screen the operator was on to scan a sticker. It is
         now a dialog over that screen, opened from the left menu, and `/qr`
         only opens it (see router/index.js), so old bookmarks keep working.

         Mounted once in App.vue and not in the left menu, because the drawer
         holding the menu is not rendered on every screen. -->
    <DialogQrScanner v-model="open" @detected="ondetect">
        <!-- Says what a scan will do right now. With a rack load open a panel
             sticker marks the panel instead of opening it, and that has to be
             visible before the camera is pointed at anything. -->
        <v-alert v-if="active_load" type="info" variant="tonal" density="compact">
            <div class="font-weight-medium">{{ t("scan_to_load") }}</div>
            <div class="text-caption">{{ t("scan_to_load_help") }}</div>
        </v-alert>
    </DialogQrScanner>
</template>

<script>
import DialogQrScanner from "@/components/fields/DialogQrScanner.vue";
import { backend_api_v2 } from "/src/utils/apiv2";
import { useAppStore } from "@/stores/app";
import { useLogisticsStore } from "@/stores/logistics";
import { usePitStoreBasics } from "@/stores/app_common";

// `/app/viewer/assembly/<uuid>` and `/app/viewer/module/<uuid>` are what the
// panel stickers carry. Anything else is not a constructible.
const CONSTRUCTIBLE_ROUTE = /\/viewer\/(assembly|module)\/([0-9a-f-]{36})/

export default {
    name: "DialogQrReader",
    components: { DialogQrScanner },
    setup() {
        return {
            ...usePitStoreBasics(),
            app_store: useAppStore(),
            logistics_store: useLogisticsStore(),
        }
    },
    computed: {
        open: {
            get() { return this.app_store.qr_reader_open },
            set(v) { this.app_store.qr_reader_open = v },
        },
        // Set only by the delivery viewer, and cleared when it unmounts. This
        // is what scopes the shortcut below: with no load open the reader
        // behaves exactly as it always has.
        active_load() {
            return this.logistics_store.active_rack_load
        },
    },
    methods: {
        go(route) {
            this.$router
                .push(route)
                .catch(() => console.log("[!] Can't update route."));
        },
        fetchUrlByClientID(client_id) {
            return backend_api_v2
                .get(`/constructibles/actuals/find/?client_order_id=${client_id}`)
                .then(({ data }) => {
                    if (data.status == "found") {
                        this.$bus.emit('notification', { timeout: 3000, text: `Redirecting to Client ID: ${client_id}`, color: "success" })
                        this.go(`/viewer/${data.level}/${data.uuid}`)
                    }
                })
                .catch((e) => this.$bus.emit('notification', { timeout: 3000, text: `Client ID not found: ${client_id}: ${e}`, color: "warning" }))
        },
        /**
         * With a rack load open, a scanned panel of that load is marked in the
         * rack instead of navigating away.
         *
         * That is the gesture on the floor: the panel goes on the rack, the
         * sticker is already in front of the camera, and nobody wants a page
         * about the panel. Returns true when it handled the code.
         */
        async markOnActiveLoad(decoded_string) {
            const load = this.active_load
            if (!load) return false
            const match = CONSTRUCTIBLE_ROUTE.exec(decoded_string)
            if (!match) return false
            const uuid = match[2]
            if (!(load.assemblies || []).includes(uuid)) {
                this.$bus.emit('notification', { timeout: 4000, text: this.t("panel_not_on_this_load"), color: "warning" })
                return true
            }
            try {
                await backend_api_v2.put(`constructibles/assemblies/${uuid}/`,
                    { uuid, delivery_status: "in_rack" })
                this.$bus.emit('notification', { timeout: 3000, text: this.t("panel_marked_in_rack"), color: "success" })
                // The dialog opens over the load, so this is usually where the
                // operator already is.
                if (this.$route.params?.trace_uuid !== load.uuid) {
                    this.go({ name: 'rack_load', params: { trace_uuid: load.uuid } })
                }
            } catch (e) {
                this.$bus.emit('notification', { timeout: 6000, text: e?.response?.data?.detail || e.message, color: "error" })
            }
            return true
        },
        standardRoutePush(decoded_string) {
            if (!decoded_string.includes("/app")) {
                this.$bus.emit('notification', { timeout: 3000, text: `Not valid QR for produuz.it`, color: "error" })
                return
            }
            let route = decoded_string.substr(
                decoded_string.indexOf("/app") + 4
            );
            this.$bus.emit('notification', { timeout: 3000, text: `Going to URL: ${route}`, color: "success" })
            this.go(route)
        },
        async ondetect(raw) {
            const decoded_string = (raw || "").trim().toLowerCase()
            this.$bus.emit('notification', { timeout: 1000, text: "Found QR. Data: " + decoded_string, color: "info" })
            try {
                if (decoded_string.startsWith("po:")) {
                    this.fetchUrlByClientID(decoded_string.substring(3))
                }
                else if (await this.markOnActiveLoad(decoded_string)) {
                    // Handled: a panel of the open rack load.
                }
                else {
                    this.standardRoutePush(decoded_string)
                }
            } catch (e) {
                this.$bus.emit('notification', { timeout: 5000, text: "Error reading the QR:" + e, color: "error" })
            }
        },
    },
};
</script>
